/**
 * Dev-only model mode (Cheap ⇄ Normal).
 *
 * In any non-production environment the user can flip every flow between the
 * real models and the cheap override models (server-side enforced — see
 * server config/devModels.ts). Persisted in localStorage and broadcast via a
 * window CustomEvent so the EnvBanner toggle stays in sync without a state
 * library (Zustand is not a dependency). Default = Cheap so dev stays free
 * unless the user explicitly opts into Normal.
 *
 * The header name + mode values come from the shared schema (single source —
 * a rename there updates both client and server).
 */

import { useEffect, useState } from "react";
import { DevModelMode, type DevModelMode as DevModelModeType } from "@shared/schemas/enums";

const STORAGE_KEY = "flowly-dev-model-mode";
const CHANGE_EVENT = "flowly-dev-model-mode-change";

export function getDevModelMode(): DevModelModeType {
  const raw = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  return raw === DevModelMode.NORMAL ? DevModelMode.NORMAL : DevModelMode.CHEAP;
}

export function setDevModelMode(mode: DevModelModeType): void {
  localStorage.setItem(STORAGE_KEY, mode);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/** React binding: current mode + setter, kept in sync across components/tabs. */
export function useDevModelMode(): [DevModelModeType, (mode: DevModelModeType) => void] {
  const [mode, setMode] = useState<DevModelModeType>(getDevModelMode);

  useEffect(() => {
    const sync = () => setMode(getDevModelMode());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync); // cross-tab
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return [mode, setDevModelMode];
}
